<script setup>
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { array, boolean, object, string } from "yup";

const { fromOptions, toOptions } = defineProps({
  fromOptions: {
    type: Array,
    required: true,
  },
  toOptions: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const form = reactive({
  from: fromOptions[0],
  to: [toOptions[0]],
  subject: "",
  body: "",
  byApikom: false,
});

const preview = ref("");

const isPreviewing = ref(false);

const isSubmitting = ref(false);

const selectedTab = ref(0);

const validationSchema = object({
  from: object().required(t("error.required")),
  to: array().min(1).required(t("error.required")),
  subject: string().required(t("error.required")),
  body: string().required(t("error.required")),
  byApikom: boolean().nullable(),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveAction() {
  isSubmitting.value = true;

  fetch(route("front.projectManagement.sem.postEmailing"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify({
      from: form.from,
      to: form.to,
      subject: form.subject,
      body: form.body,
      byApikom: form.byApikom,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      toast.success(t("toast.successfullySent"), {
        position: "top-right",
      });
    })
    .finally(() => {
      isSubmitting.value = false;
      router.reload();
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
    });
}

function previewAction() {
  isPreviewing.value = true;

  fetch(route("front.projectManagement.sem.postPreviewEmailing"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify({
      body: form.body,
      byApikom: form.byApikom,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then((data) => {
      preview.value = data.preview;
    })
    .finally(() => {
      isPreviewing.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
    });
}

watch(selectedTab, (newValue) => {
  if (newValue === 1) {
    previewAction();
  }
});
</script>

<template>
  <Form
    v-slot="{ errors }"
    :validation-schema="validationSchema"
    @submit="saveAction"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
        preview: t('title.preview'),
      }"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <ListboxField
              v-model="form.from"
              field-id="from"
              field-name="from"
              :label="t('label.from')"
              :options="fromOptions"
              required
            >
              <template #buttonContent>
                <template v-if="form.from">
                  {{ form.from.name }}
                </template>

                <template v-else> &nbsp;</template>
              </template>

              <template #optionContent="{ option }">
                {{ option.name }} - {{ option.email }}
              </template>
            </ListboxField>
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <ComboboxField
              v-model="form.to"
              field-id="to"
              field-name="to"
              :label="t('label.to')"
              :options="toOptions"
              filtered-option-key="email"
              multiple
              required
            >
              <template #optionContent="{ option }">
                <template v-if="option.tenant">
                  <b>{{ option.tenant.name }}&nbsp;:</b>&nbsp;
                </template>
                {{ option.name }} - {{ option.email }}
              </template>
            </ComboboxField>
          </div>

          <InputField
            v-model="form.subject"
            :label="t('label.subject')"
            field-name="subject"
            field-id="subject"
            required
          />

          <WysiwygField
            v-model="form.body"
            :label="t('label.body')"
            field-name="body"
            field-id="body"
            required
          />

          <SwitchField
            v-model="form.byApikom"
            :label="t('label.byApikom')"
            field-id="byApikom"
            field-name="byApikom"
          />
        </TabPanel>

        <TabPanel unmount>
          <LoadingSpinner v-if="isPreviewing" />

          <template v-else>
            <div v-html="preview"></div>
          </template>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      v-if="selectedTab === 0"
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
        :loading="isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.send") }}
        </template>

        <template v-else>
          {{ t("button.sending") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
